import { Alert, Button, Input, Modal } from "../../ds";

// Die beiden Dialoge des Uplink-Schreibpfads: Formular und Bestätigung.
// Beide schreiben nichts selbst — das Ergebnis geht als `wg_set` durch die
// Sektion (Probe + Auto-Rollback).

/** Frist des Agents: länger als bei der Firewall, weil ein rotierter Schlüssel
 *  erst zentral eingetragen werden muss (docs/wire-protocol-v1.md). */
export const WG_REVERT_IN = 300;

export interface WgForm {
  peerPublicKey: string;
  endpoint: string;
  allowedIps: string;
  keepalive: string;
  address: string;
}

/** Änderung, die noch durch den Bestätigungsdialog muss. */
export type WgPending = { kind: "set"; params: Record<string, unknown> } | { kind: "rotate" };

/** Feld → Label + Beispiel. Reihenfolge = Anzeigereihenfolge. */
const FIELDS: [keyof WgForm, string, string][] = [
  ["peerPublicKey", "Public Key des WG-Servers", "abc…="],
  ["endpoint", "Endpoint", "wireguard.eulektro.de:51820"],
  ["allowedIps", "AllowedIPs (kommagetrennt)", "10.88.0.0/16"],
  ["keepalive", "Persistent Keepalive in Sekunden", "25"],
  ["address", "Eigene Adresse des Routers", "10.88.3.11/32"],
];

/** Formular → flache `wg_set`-Params. Leere Felder bleiben weg; der Agent
 *  lässt sie dann unverändert. */
function toParams(form: WgForm): Record<string, unknown> {
  const allowedIps = form.allowedIps
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const params: Record<string, unknown> = {};
  if (form.peerPublicKey.trim()) params.peer_public_key = form.peerPublicKey.trim();
  if (form.endpoint.trim()) params.endpoint = form.endpoint.trim();
  if (allowedIps.length) params.allowed_ips = allowedIps;
  if (form.keepalive.trim()) params.persistent_keepalive = Number(form.keepalive);
  if (form.address.trim()) params.address = form.address.trim();
  return params;
}

export function WgUplinkModal({
  form,
  open,
  onChange,
  onClose,
  onSubmit,
}: {
  form: WgForm | null;
  open: boolean;
  onChange: (form: WgForm) => void;
  onClose: () => void;
  onSubmit: (params: Record<string, unknown>) => void;
}) {
  return (
    <Modal
      open={open && form !== null}
      onClose={onClose}
      title="EulektroWG-Uplink bearbeiten"
      subtitle="Die Werte gehen erst nach der Bestätigung auf Probe zum Router."
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            iconName="check"
            onClick={() => form && onSubmit(toParams(form))}
          >
            Weiter
          </Button>
        </>
      }
    >
      {form && (
        <div className="flex flex-col gap-4">
          <Alert variant="info">
            Leere Felder bleiben unverändert. Der private Schlüssel des Routers wird hier nie
            angezeigt oder gesetzt — dafür gibt es „Schlüssel rotieren“.
          </Alert>
          {FIELDS.map(([field, label, placeholder]) => (
            <Input
              key={field}
              label={label}
              placeholder={placeholder}
              value={form[field]}
              onChange={(e) => onChange({ ...form, [field]: e.target.value })}
            />
          ))}
        </div>
      )}
    </Modal>
  );
}

/** Letzter Halt vor dem Schreiben — je nach Art mit eigener Warnung. */
export function WgConfirmModal({
  pending,
  busy,
  onClose,
  onConfirm,
}: {
  pending: WgPending | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const rotate = pending?.kind === "rotate";
  return (
    <Modal
      open={pending !== null}
      onClose={onClose}
      title={rotate ? "Schlüsselpaar neu erzeugen?" : "Uplink ändern?"}
      size="sm"
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="danger" iconName="shield" loading={busy} onClick={onConfirm}>
            Auf Probe anwenden
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        <Alert
          variant="danger"
          title={rotate ? "Der Tunnel steht bis zum zentralen Eintrag" : "Der Uplink kann brechen"}
        >
          {rotate
            ? "Der Router erzeugt ein neues Schlüsselpaar; der private Teil verlässt ihn nie. Bis der neue öffentliche Schlüssel auf wireguard.eulektro.de hinterlegt ist, kommt kein Handshake zustande."
            : "Über den EulektroWG-Uplink läuft der Zugriff auf alles hinter diesem Router. Stimmen Peer-Key, Endpoint oder AllowedIPs nicht, ist er weg."}
        </Alert>
        <p className="text-content-2">
          Deshalb wird nur <strong>auf Probe</strong> angewendet: Der Router sichert die alte
          Konfiguration und stellt sie nach {WG_REVERT_IN} Sekunden automatisch wieder her — es sei
          denn, du bestätigst vorher mit „Behalten“.
        </p>
      </div>
    </Modal>
  );
}
